import React from "react";
import {
  FaEnvelope,
  FaLock,
  FaShieldAlt
} from "react-icons/fa";

import "../assets/css/privacy-policy.css";

const policySections = [
  {
    id: "information-we-collect",
    number: "01",
    title: "Information We Collect",
    content: (
      <>
        <p>
          We may collect information that you voluntarily provide when you
          contact us, request information, submit an inquiry, respond to a
          business opportunity, or otherwise communicate with Bethel
          Innovations.
        </p>

        <p>This information may include:</p>

        <ul>
          <li>Your name, organization, job title, and contact information.</li>

          <li>
            Information included in messages, requests, or business inquiries.
          </li>

          <li>
            Professional information submitted in connection with employment,
            teaming, vendor, or subcontracting opportunities.
          </li>

          <li>
            Other information you choose to provide through the website.
          </li>
        </ul>

        <p>
          We may also receive limited technical information automatically when
          you visit the website. This may include your browser type, device
          type, operating system, referring website, approximate location
          derived from your IP address, and pages visited.
        </p>
      </>
    )
  },
  {
    id: "how-we-use-information",
    number: "02",
    title: "How We Use Information",
    content: (
      <>
        <p>We may use collected information to:</p>

        <ul>
          <li>Respond to questions, requests, and business inquiries.</li>

          <li>
            Provide information about our services and organizational
            capabilities.
          </li>

          <li>
            Evaluate employment, teaming, vendor, or subcontracting
            opportunities.
          </li>

          <li>Operate, maintain, secure, and improve our website.</li>

          <li>
            Monitor website performance and understand how visitors interact
            with website content.
          </li>

          <li>
            Protect our organization, systems, users, and legitimate business
            interests.
          </li>

          <li>
            Satisfy applicable legal, regulatory, contractual, and compliance
            obligations.
          </li>
        </ul>

        <p>
          We do not use information submitted through this website for purposes
          that are materially unrelated to the reason it was provided without
          appropriate notice.
        </p>
      </>
    )
  },
  {
    id: "cookies-and-analytics",
    number: "03",
    title: "Cookies and Website Analytics",
    content: (
      <>
        <p>
          Our website may use cookies and similar technologies to support
          essential website functionality, security, performance measurement,
          and website improvement. Cookies are small data files stored on your
          browser or device.
        </p>

        <p>
          Depending on the services enabled on the website, analytics
          providers may collect information such as pages visited, approximate
          location, device information, referral sources, and interactions
          with website features.
        </p>

        <p>
          You can manage or disable cookies through your browser settings.
          Disabling certain cookies may affect the operation or availability
          of some website features.
        </p>
      </>
    )
  },
  {
    id: "information-sharing",
    number: "04",
    title: "How We Share Information",
    content: (
      <>
        <p>
          Bethel Innovations does not sell personal information collected
          through this website.
        </p>

        <p>We may disclose information in limited circumstances, including:</p>

        <ul>
          <li>
            To service providers that support website hosting, security,
            communications, analytics, or other business operations.
          </li>

          <li>
            When reasonably necessary to respond to an authorized government,
            regulatory, judicial, or legal request.
          </li>

          <li>
            To protect the rights, safety, property, systems, or operations of
            Bethel Innovations and others.
          </li>

          <li>
            In connection with a merger, acquisition, reorganization, transfer
            of assets, or other legitimate business transaction.
          </li>

          <li>When you have authorized or directed the disclosure.</li>
        </ul>

        <p>
          Service providers are permitted to access information only as
          necessary to perform services on our behalf and are subject to
          applicable contractual requirements.
        </p>
      </>
    )
  },
  {
    id: "data-security",
    number: "05",
    title: "Data Security",
    content: (
      <>
        <p>
          We use administrative, technical, and physical safeguards intended
          to protect information against unauthorized access, loss, misuse,
          alteration, and disclosure. Safeguards may include access controls,
          secure system configurations, monitoring, and other risk-based
          security measures.
        </p>

        <p>
          No website, network, transmission method, or storage system can be
          guaranteed to be completely secure. You should avoid sending
          classified information, controlled unclassified information,
          protected health information, financial account information,
          passwords, or other highly sensitive information through public
          website forms or unencrypted email.
        </p>
      </>
    )
  },
  {
    id: "government-information",
    number: "06",
    title: "Government and Contract Information",
    content: (
      <>
        <p>
          This public website is not intended for transmitting classified
          information, controlled unclassified information, source-selection
          information, procurement-sensitive material, export-controlled
          technical data, or information protected by contractual handling
          requirements.
        </p>

        <p>
          Authorized clients, partners, subcontractors, and government
          personnel should use approved communication channels and information
          systems designated for the applicable engagement.
        </p>
      </>
    )
  },
  {
    id: "data-retention",
    number: "07",
    title: "Data Retention",
    content: (
      <p>
        We retain personal information only for as long as reasonably necessary
        to fulfill the purposes described in this policy, maintain appropriate
        business records, resolve disputes, enforce agreements, and comply with
        applicable legal, contractual, and regulatory obligations. Retention
        periods may vary depending on the type of information and the reason it
        was collected.
      </p>
    )
  },
  {
    id: "your-choices",
    number: "08",
    title: "Your Privacy Choices",
    content: (
      <>
        <p>
          Subject to applicable law, you may request that we review, correct,
          or delete personal information you previously provided through this
          website. Certain information may need to be retained when required
          by law, contract, security requirements, or legitimate business
          recordkeeping obligations.
        </p>

        <p>
          To submit a privacy-related request, contact us at{" "}
          <a href="mailto:info@bethelinnovations.com">
            info@bethelinnovations.com
          </a>
          . We may take reasonable steps to verify your identity before
          responding to a request.
        </p>
      </>
    )
  },
  {
    id: "third-party-links",
    number: "09",
    title: "Third-Party Websites",
    content: (
      <p>
        Our website may contain links to government agencies, business
        partners, social-media platforms, and other third-party websites.
        Bethel Innovations does not control and is not responsible for the
        content, security, availability, or privacy practices of those
        websites. Review the applicable third party&apos;s privacy policy
        before providing personal information.
      </p>
    )
  },
  {
    id: "children",
    number: "10",
    title: "Children’s Privacy",
    content: (
      <p>
        This website is intended for business and professional audiences and is
        not directed to children under 13. We do not knowingly collect personal
        information from children under 13 through this website. If you believe
        a child has submitted personal information, please contact us so we can
        review the matter.
      </p>
    )
  },
  {
    id: "policy-updates",
    number: "11",
    title: "Changes to This Policy",
    content: (
      <p>
        We may update this Privacy Policy to reflect changes in our website,
        business practices, technology, or legal obligations. The revised
        policy will be posted on this page with an updated effective date.
        Continued use of the website after an update is subject to the revised
        policy.
      </p>
    )
  }
];

const PrivacyPolicy = () => {
  return (
    <main className="privacy-page">
      {/* HERO */}
      <section
        className="privacy-hero"
        aria-labelledby="privacy-page-heading"
      >
        <div className="privacy-hero__container">
          <div className="privacy-hero__content">
            <span className="privacy-hero__label">
              Legal &amp; Compliance
            </span>

            <h1 id="privacy-page-heading">Privacy Policy</h1>

            <p>
              Learn how Bethel Innovations, LLC collects, uses, protects, and
              manages information received through our public website.
            </p>

            <div className="privacy-hero__meta">
              <span>Effective August 12, 2026</span>
              <span aria-hidden="true" />
              <span>Last reviewed August 12, 2026</span>
            </div>
          </div>
        </div>

        <div className="privacy-hero__accent" aria-hidden="true" />
      </section>

      {/* INTRODUCTION */}
      <section className="privacy-introduction">
        <div className="privacy-container privacy-introduction__layout">
          <div>
            <span className="privacy-eyebrow">Our Commitment</span>

            <h2>Responsible information handling builds trust.</h2>
          </div>

          <div className="privacy-introduction__copy">
            <p>
              Bethel Innovations respects the privacy of website visitors,
              clients, partners, applicants, and others who communicate with
              us.
            </p>

            <p>
              This policy applies to information collected through our public
              website. It does not replace project-specific, employment,
              contractual, government, or system-level privacy notices that
              may apply in other contexts.
            </p>
          </div>
        </div>
      </section>

      {/* SECURITY NOTICE */}
      <section className="privacy-security">
        <div className="privacy-container">
          <div className="privacy-security__notice">
            <div className="privacy-security__icon">
              <FaShieldAlt aria-hidden="true" />
            </div>

            <div>
              <h2>Public Website Security Notice</h2>

              <p>
                Do not submit classified, controlled, procurement-sensitive,
                export-controlled, or other restricted information through
                this website or by general email. Use only communication
                channels authorized for the applicable engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POLICY DOCUMENT */}
      <section className="privacy-document">
        <div className="privacy-container privacy-document__layout">
          {/* TABLE OF CONTENTS */}
          <aside className="privacy-toc">
            <div className="privacy-toc__inner">
              <span className="privacy-toc__heading">On This Page</span>

              <nav aria-label="Privacy Policy contents">
                <ol>
                  {policySections.map((section) => (
                    <li key={section.id}>
                      <a href={`#${section.id}`}>
                        <span>{section.number}</span>
                        {section.title}
                      </a>
                    </li>
                  ))}

                  <li>
                    <a href="#contact-us">
                      <span>12</span>
                      Contact Us
                    </a>
                  </li>
                </ol>
              </nav>
            </div>
          </aside>

          {/* POLICY CONTENT */}
          <div className="privacy-policy">
            <header className="privacy-policy__header">
              <span>Bethel Innovations, LLC</span>

              <h2>Website Privacy Policy</h2>

              <p>
                The following sections describe our general website
                information practices and the choices available to website
                visitors.
              </p>
            </header>

            {policySections.map((section) => (
              <section
                className="privacy-policy__section"
                id={section.id}
                key={section.id}
              >
                <div className="privacy-policy__heading">
                  <span>{section.number}</span>
                  <h2>{section.title}</h2>
                </div>

                <div className="privacy-policy__body">
                  {section.content}
                </div>
              </section>
            ))}

            {/* CONTACT */}
            <section
              className="privacy-policy__section privacy-policy__contact-section"
              id="contact-us"
            >
              <div className="privacy-policy__heading">
                <span>12</span>
                <h2>Contact Us</h2>
              </div>

              <div className="privacy-policy__body">
                <p>
                  Contact Bethel Innovations if you have questions about this
                  policy or our website information practices.
                </p>

                <address className="privacy-contact">
                  <div className="privacy-contact__icon">
                    <FaEnvelope aria-hidden="true" />
                  </div>

                  <div>
                    <strong>Bethel Innovations, LLC</strong>
                    <span>Privacy and website inquiries</span>

                    <a href="mailto:info@bethelinnovations.com">
                      info@bethelinnovations.com
                    </a>
                  </div>
                </address>
              </div>
            </section>

            <div className="privacy-policy__footer-note">
              <FaLock aria-hidden="true" />

              <p>
                This public website is not an authorized system for
                transmitting sensitive government or contract information.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;